/**
 * O que corre por cima de todas as páginas do painel da Escola: o `/vs` de 10 em 10 s (as
 * notificações e a mudança do tipo de acesso), o heartbeat do monitoramento, o aviso das
 * exportações em segundo plano e três avisos visuais:
 *
 * - «Pagamento pendente» e «Acesso a expirar»: cartões no canto inferior direito, empilhados
 *   (no telemóvel ficam por cima da barra de baixo do casco);
 * - «Sessão terminada»: uma janela que não se fecha — só se sai dela a actualizar a página.
 */
import { ReactNode, useEffect, useRef, useState } from "react";
import { router, usePage } from "@inertiajs/react";
import { Icon } from "@iconify/react";
import Countdown, { type CountdownRenderProps } from "react-countdown";
import moment from "moment";
import "moment/dist/locale/pt";
import { Botao, BotaoIcone, IconeTema, Janela } from "@/Logigate/ui";
import { publish, EVENTO } from "@/utils/events";
import { formataDinheiro, toPad } from "@/utils/ForAll";
import req from "@/utils/req";
import { useBasePath } from "@/Escola/hooks/useBasePath";
import { useMonitoramentoHeartbeat } from "@/Logigate/hooks/monitoramento";
import { useExportNotification } from "@/Logigate/hooks/useExportNotification";

// O resto da Escola conta com o moment em português (era este ficheiro que o ligava).
moment.locale("pt");

interface MainProviderProps {
    children: ReactNode;
}

export function MainProvider({ children }: MainProviderProps) {
    const interval = useRef<NodeJS.Timeout | null>(null);
    const running = useRef(false);
    const { props } = usePage<any>();
    const [openAuthModal, setOpenAuthModal] = useState(false);
    const [showPaymentNotice, setShowPaymentNotice] = useState(false);
    const [showAccessNotice, setShowAccessNotice] = useState(false);
    const [acessoEscondido, setAcessoEscondido] = useState(false);
    const [aActualizar, setAActualizar] = useState(false);
    const basePath = useBasePath();

    // Monitoramento heartbeat - activo apenas quando acedido directamente (não via Logigate)
    const monitoramentoEnabled = props.monitoramento_enabled === true
        && props.context === "escola" && !!props.auth?.user;

    useMonitoramentoHeartbeat({
        enabled: monitoramentoEnabled,
        intervaloMs: 30000,
        basePath: basePath(""),
        fonteCodigo: "escola",
    });

    // Notificação em tempo real para exportações em segundo plano
    useExportNotification({
        channelName: `escola.secretaria.${props.sec?.id}`,
        enabled: !!props.sec?.id,
        processosUrl: "/processos",
    });

    function reload() {
        router.reload({
            only: ["acesso_at", "sec", "progress", "total_n"],
        });
    }

    const verifica = async () => {
        if (props.auth?.user) {
            try {
                const response = await req().post("/vs");

                if (response.data.estado === "ok") {
                    publish(EVENTO.NOTIFICA, {
                        total_n: response.data.total_n || 0,
                        ...response.data,
                    });

                    if (response.data.tipo_acesso !== props.sec?.tipo_acesso) {
                        reload();
                    }
                } else if ((response.data as any).codigo === "ESCOLA_ENCERRADA") {
                    // A escola fechou (rescisão do contrato) com o painel aberto: a página inteira
                    // recarrega e mostra o ecrã de escola fechada.
                    window.location.reload();
                } else {
                    if (response.data.auth) {
                        setOpenAuthModal(true);
                    }
                }
            } catch (error) {
                console.error("Erro ao verificar status:", error);
            }
        }
        running.current = false;
    };

    useEffect(() => {
        // Verificação inicial
        verifica();

        // Configura intervalo de verificação
        interval.current = setInterval(() => {
            if (!running.current) {
                running.current = true;
                verifica();
            }
        }, 10000);

        // Mostra os avisos de pagamento e de acesso se for preciso
        if (props.p_g && props.auth?.user && !props.force_G) {
            setShowPaymentNotice(true);
        }

        if (props.acesso_at && props.auth?.user) {
            setShowAccessNotice(true);
        }

        return () => {
            if (interval.current !== null) {
                clearInterval(interval.current);
            }
        };
    }, []);

    const mostrarPagamento = showPaymentNotice && !!props.p_g;
    const mostrarAcesso = showAccessNotice && !!props.acesso_at;

    return (
        <>
            {children}

            {(mostrarPagamento || mostrarAcesso) && (
                <div
                    className="pointer-events-none fixed inset-x-3 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-[45] flex flex-col items-stretch gap-3 sm:inset-x-auto sm:right-5 sm:w-[22rem] md:bottom-5 print:hidden"
                    aria-live="polite"
                >
                    {mostrarAcesso && (
                        // O contador continua a correr com o cartão escondido: é ele que actualiza
                        // a página quando o acesso acaba (e nessa altura o cartão volta a aparecer).
                        <Countdown
                            key={String(props.acesso_at)}
                            date={props.acesso_at}
                            onComplete={() => {
                                setAcessoEscondido(false);
                                reload();
                            }}
                            renderer={(tempo) => (
                                <CartaoAcesso tempo={tempo} escondido={acessoEscondido} onEsconder={() => setAcessoEscondido(true)} />
                            )}
                        />
                    )}

                    {mostrarPagamento && (
                        <CartaoCanto
                            cor="amber"
                            icone={<Icon icon="mdi:receipt-text-clock-outline" />}
                            titulo="Pagamento pendente"
                            onEsconder={() => setShowPaymentNotice(false)}
                        >
                            <p>
                                Há uma taxa pendente de{" "}
                                <strong className="font-semibold text-zinc-900 dark:text-zinc-50">{formataDinheiro(props.p_g.total)}</strong>{" "}
                                associada aos serviços da Escola Inteligente.
                            </p>
                            <Botao
                                tamanho="sm"
                                href={basePath("/configuracoes/pagamentos")}
                                iconeDireita={<Icon icon="mdi:arrow-right" className="size-4" />}
                                className="mt-3 h-11 w-full sm:h-8 sm:w-auto"
                            >
                                Ver
                            </Botao>
                        </CartaoCanto>
                    )}
                </div>
            )}

            {/* Sessão terminada: não se fecha (sem X, sem Esc, sem tocar fora). */}
            <Janela
                aberta={openAuthModal}
                onFechar={() => {}}
                fecharAoClicarFora={false}
                confirmarAoFechar={false}
                largura="sm"
                className="[&_[aria-label=Fechar]]:hidden"
            >
                <div className="flex flex-col items-center px-1 pb-1 pt-4 text-center">
                    <IconeTema cor="red" tamanho="xl" redondo>
                        <Icon icon="mdi:account-lock-outline" />
                    </IconeTema>
                    <h2 className="mt-4 text-lg font-semibold text-zinc-900 dark:text-zinc-50">A sessão terminou</h2>
                    <p className="mt-1.5 text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
                        Parece que já não tens sessão iniciada. Actualiza a página para continuar.
                    </p>
                    <Botao
                        larguraTotal
                        tamanho="lg"
                        className="mt-6"
                        icone={<Icon icon="mdi:refresh" className="size-5" />}
                        aCarregar={aActualizar}
                        onClick={() => {
                            setAActualizar(true);
                            window.location.reload();
                        }}
                    >
                        Actualizar agora
                    </Botao>
                </div>
            </Janela>

            {/*
              O Agente Silva deixou de ter widget flutuante próprio: vive agora como
              conversa fixada no dock do MakaChat (ver Components/MakaChat/PonteSilva).
            */}
        </>
    );
}

const ACENTOS = {
    amber: { barra: "bg-amber-500", icone: "bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400" },
    red: { barra: "bg-red-500", icone: "bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400" },
} as const;

/** Um cartão do canto: a barra de cor à esquerda, o ícone, o título, o texto e o X. */
function CartaoCanto({ cor, icone, titulo, onEsconder, escondido, children }: {
    cor: keyof typeof ACENTOS;
    icone: ReactNode;
    titulo: ReactNode;
    onEsconder: () => void;
    escondido?: boolean;
    children: ReactNode;
}) {
    const acento = ACENTOS[cor];
    return (
        <div
            role="status"
            hidden={escondido}
            className="pointer-events-auto relative overflow-hidden rounded-xl bg-white shadow-lg shadow-zinc-950/10 ring-1 ring-zinc-950/10 animate-in fade-in-0 slide-in-from-bottom-2 duration-300 dark:bg-zinc-900 dark:shadow-black/40 dark:ring-white/10"
        >
            <span className={`absolute inset-y-0 left-0 w-1 ${acento.barra}`} aria-hidden />
            <div className="flex gap-3 py-3.5 pl-5 pr-2">
                <span className={`flex size-9 shrink-0 items-center justify-center rounded-lg text-[20px] ${acento.icone}`}>{icone}</span>
                <div className="min-w-0 flex-1 pt-0.5 text-[13px] leading-relaxed text-zinc-600 dark:text-zinc-300">
                    <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{titulo}</p>
                    <div className="mt-0.5">{children}</div>
                </div>
                <BotaoIcone rotulo="Esconder" tamanho="lg" className="-mt-1 size-11 sm:size-8" onClick={onEsconder}>
                    <Icon icon="mdi:close" className="size-4" />
                </BotaoIcone>
            </div>
        </div>
    );
}

/** O «Acesso a expirar», com a contagem decrescente em dias, horas, minutos e segundos. */
function CartaoAcesso({ tempo, escondido, onEsconder }: { tempo: CountdownRenderProps; escondido: boolean; onEsconder: () => void }) {
    const { days, hours, minutes, seconds, completed } = tempo;
    const partes = [
        { valor: days, rotulo: "dias" },
        { valor: hours, rotulo: "horas" },
        { valor: minutes, rotulo: "min" },
        { valor: seconds, rotulo: "seg" },
    ];

    return (
        <CartaoCanto
            cor="red"
            icone={<Icon icon="mdi:timer-sand" />}
            titulo={completed ? "O acesso terminou" : "Acesso a expirar"}
            onEsconder={onEsconder}
            escondido={escondido}
        >
            {completed ? (
                <p className="font-medium text-emerald-700 dark:text-emerald-400">Tempo esgotado. A página vai actualizar-se em instantes.</p>
            ) : (
                <>
                    <p>O teu acesso vai expirar em:</p>
                    <div className="mt-2 grid grid-cols-4 gap-1.5" aria-label={`${days} dias, ${hours} horas, ${minutes} minutos e ${seconds} segundos`}>
                        {partes.map((p) => (
                            <div key={p.rotulo} className="rounded-lg bg-zinc-50 px-1 py-1.5 text-center ring-1 ring-inset ring-zinc-950/5 dark:bg-zinc-800/60 dark:ring-white/5">
                                <span className="block text-base font-semibold tabular-nums leading-5 text-zinc-900 dark:text-zinc-50">{toPad(p.valor, 2)}</span>
                                <span className="block text-[10px] font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">{p.rotulo}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </CartaoCanto>
    );
}
